// WorkspaceSwitcher — the pure workspace picker, shared by the Settings
// Account surface and the rail's account popover so the two entry points read
// the SAME switcher (never two drifting copies of the collapse rule).
//
// Collapses to a plain label + role badge when there is nothing to switch
// between (the live single-workspace-per-user default), and only shows a
// Select once a second reachable workspace exists — matching
// `sketches/iam-shell/IamShell.tsx`'s `SingleWorkspaceLocalFirst` story.
// Extracted verbatim from Account (IDD 94442b9d) when the rail gained a
// visible account entry point; Account now composes this instead of inlining
// it.
//
// Pure/props-driven: no state, no I/O.

export type { AccountWorkspace };
export { WorkspaceSwitcher };

import { useLingui } from "@lingui/react/macro";
import type { WorkspaceRole } from "../../../app/ports/iam.js";
import { Badge } from "../../Badge/Badge.js";
import { Icon } from "../../Icon/Icon.js";
import { Select } from "../../Select/Select.js";
import { Text } from "../../Text/Text.js";
import { useWorkspaceRoleLabel } from "../useWorkspaceRoleLabel.js";
import "./WorkspaceSwitcher.css";

// A workspace as the switcher needs it: a pre-resolved label (there is no
// `workspaces.title` column on the live schema — a label is a presentation
// concern the CALLER derives, never invented here) plus the caller's role.
type AccountWorkspace = {
    readonly id: string;
    readonly label: string;
    readonly role: WorkspaceRole;
};

type WorkspaceSwitcherProps = {
    readonly workspaces: readonly AccountWorkspace[];
    readonly activeWorkspaceId: string;
    readonly onSwitchWorkspace: (workspaceId: string) => void;
};

const WorkspaceSwitcher = ({
    workspaces,
    activeWorkspaceId,
    onSwitchWorkspace,
}: WorkspaceSwitcherProps) => {
    const { t } = useLingui();
    const roleLabel = useWorkspaceRoleLabel();
    const active = workspaces.find((w) => w.id === activeWorkspaceId);
    // Nothing to switch between → a plain label, no dropdown chrome at all
    // (not a disabled-looking single-option control) — the sketch's own
    // "single workspace, local-first" reading.
    const singleWorkspace = workspaces.length <= 1;

    if (singleWorkspace) {
        return (
            <div className="bilda-workspace-switcher__row">
                <Icon name="workspace" size="sm" />
                <Text size="sm">{active?.label ?? t`Your workspace`}</Text>
                {active ? (
                    <Badge tone="neutral">{roleLabel(active.role)}</Badge>
                ) : null}
            </div>
        );
    }
    return (
        <Select
            ariaLabel={t`Workspace`}
            options={workspaces.map((w) => ({ value: w.id, label: w.label }))}
            value={activeWorkspaceId}
            onValueChange={onSwitchWorkspace}
        />
    );
};
